import { useState, useEffect, useRef } from "react";
import {
  Wrench,
  Settings,
  ShieldCheck,
  Monitor,
  Zap,
  SearchCheck,
} from "lucide-react";

const colors = {
  navy: "#0A1628",
  navyLight: "#0F2040",
  blue: "#1565C0",
  blueLight: "#1E88E5",
  blueBright: "#42A5F5",
  accent: "#00B4D8",
  accentLight: "#48CAE4",
  white: "#FFFFFF",
  offWhite: "#F0F4FA",
  lightGray: "#E8EEF8",
  midGray: "#8FA3C8",
  dark: "#06101E",
  cardBg: "#0D1E35",
  glass: "rgba(255,255,255,0.05)",
  glassHover: "rgba(255,255,255,0.1)",
};

const services = [
  {
    icon: <Wrench size={28} color={colors.white} />,
    title: "Manutenção de Lavadoras de Alta Pressão",
    desc: "Reparo completo em lavadoras de alta pressão Karcher, Wap e similares. Conserto de vazamentos, perda de pressão e falhas elétricas.",
  },
  {
    icon: <Settings size={28} color={colors.white} />,
    title: "Conserto de Equipamentos de Limpeza",
    desc: "Assistência técnica especializada em enceradeiras, aspiradores, compressores e lavadoras profissionais.",
  },
  {
    icon: <SearchCheck size={28} color={colors.white} />,
    title: "Diagnóstico Técnico Especializado",
    desc: "Identificação rápida de defeitos em lavadoras de alta pressão e equipamentos elétricos, com orçamento transparente.",
  },
  {
    icon: <ShieldCheck size={28} color={colors.white} />,
    title: "Manutenção Preventiva e Corretiva",
    desc: "Evite prejuízos e paradas inesperadas com revisões periódicas em lavadoras de alta pressão, enceradeiras, compressores e equipamentos industriais.",
  },
  {
    icon: <Monitor size={28} color={colors.white} />,
    title: "Suporte Técnico Remoto",
    desc: "Orientação rápida para problemas em lavadoras de alta pressão e equipamentos de limpeza.",
  },
  {
    icon: <Zap size={28} color={colors.white} />,
    title: "Atendimento Rápido em Brasília",
    desc: "Conserto de lavadoras de alta pressão com opção de retirada e entrega (a combinar). Atendemos toda a região.",
  },
];

// ── Intersection observer hook
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]); // threshold é primitivo — seguro no array de deps

  return [ref, inView];
}

// ─────────────── SERVICES ───────────────

const Services = () => {
  const [ref, inView] = useInView();
  return (
    <section
      id="servicos"
      style={{ padding: "100px 24px", background: colors.dark }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* Header */}
        <div
          ref={ref}
          className={inView ? "visible" : "hidden"}
          style={{ textAlign: "center", marginBottom: 64 }}
        >
          <div
            style={{
              display: "inline-block",
              background: "rgba(21,101,192,0.1)",
              border: `1px solid rgba(21,101,192,0.3)`,
              borderRadius: 100,
              padding: "6px 18px",
              marginBottom: 16,
            }}
          >
            <span
              style={{
                color: colors.blueBright,
                fontSize: 13,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
              }}
            >
              O que fazemos
            </span>
          </div>
          <h2
            style={{
              fontFamily: "'Syne', sans-serif",
              fontSize: "clamp(2rem, 4vw, 2.8rem)",
              fontWeight: 800,
              color: colors.white,
              marginBottom: 16,
            }}
          >
            Nossos <span className="gradient-text">Serviços</span>
          </h2>
          <p
            style={{
              color: colors.midGray,
              fontSize: 17,
              maxWidth: 480,
              margin: "0 auto",
            }}
          >
            Soluções completas em assistência técnica para manter seus
            equipamentos funcionando com eficiência.
          </p>
        </div>

        {/* Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 20,
          }}
        >
          {services.map(({ icon, title, desc }, i) => (
            <div
              key={title}
              className="service-card"
              style={{
                background: colors.cardBg,
                border: `1px solid rgba(255,255,255,0.07)`,
                borderRadius: 18,
                padding: "32px 28px",
                animation: inView
                  ? `fadeInUp 0.6s ease ${i * 0.1}s both`
                  : "none",
              }}
            >
              <div
                className="card-icon"
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 14,
                  background: `linear-gradient(135deg, ${colors.blue}, ${colors.blueLight})`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 20,
                }}
              >
                {icon}
              </div>
              <h3
                style={{
                  fontFamily: "'Syne', sans-serif",
                  fontSize: 18,
                  fontWeight: 700,
                  color: colors.white,
                  marginBottom: 10,
                }}
              >
                {title}
              </h3>
              <p
                style={{
                  color: colors.midGray,
                  fontSize: 15,
                  lineHeight: 1.65,
                }}
              >
                {desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
